/**
 * 1:1 React rebuild of the Projects dashboard (Figma node 10154:452), as a
 * decorative skeleton-style preview. Authored at a fixed design size
 * (DASH_W × DASH_H) so the camera can scale/translate it crisply.
 */

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bar } from "./primitives";
import { DASH_W, DASH_H } from "./camera";
import { Icon, type IconName } from "@/components/ui/icon";

const SIDEBAR_W = 236;
const TOPNAV_H = 64;

const FIGMA_MAIN_W = 1703.302;
const FIGMA_MAIN_H = 1160.657;
const FIGMA_MAIN_SCALE = (DASH_W - SIDEBAR_W) / FIGMA_MAIN_W;

const GRADIENTS = [
  "linear-gradient(135deg, #34d399 0%, #3b82f6 100%)",
  "linear-gradient(135deg, #7c3aed 0%, #4338ca 100%)",
  "linear-gradient(135deg, #22c55e 0%, #065f46 100%)",
];

export interface DashboardMockupPreview {
  organizationName?: string | null;
  organizationLogoUrl?: string | null;
  projectTitle?: string | null;
  projectCoverUrl?: string | null;
}

function filledText(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "";
}

function textWithFallback(value: string | null | undefined, fallback: string) {
  return filledText(value) || fallback;
}

export function DashboardMockup({ preview }: { preview?: DashboardMockupPreview }) {
  const organizationName = textWithFallback(preview?.organizationName, "SkillTheory");
  const projectTitle = textWithFallback(preview?.projectTitle, "Project Title");
  const projectCardTitle = filledText(preview?.projectTitle);
  const organizationLogoUrl = filledText(preview?.organizationLogoUrl);
  const projectCoverUrl = filledText(preview?.projectCoverUrl);

  return (
    <div
      className="absolute left-0 top-0 flex overflow-hidden text-white"
      style={{ width: DASH_W, height: DASH_H, background: "#0e0e10" }}
    >
      <Sidebar />
      <section className="flex min-w-0 flex-1 flex-col">
        <TopNav
          organizationLogoUrl={organizationLogoUrl}
          organizationName={organizationName}
          projectTitle={projectTitle}
        />
        <div className="flex min-h-0 flex-1">
          <FigmaMainCanvas
            projectCoverUrl={projectCoverUrl}
            projectCardTitle={projectCardTitle}
          />
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ sidebar */
/*
 * 1:1 rebuild of Figma node 10154:839. Real Central icons + section headings,
 * with skeleton bars standing in for the nav item labels — exactly as the
 * source design renders. Figma authored at a 1.256× export scale; values here
 * are the design's base units (Figma px ÷ 1.256).
 */

const SKELETON = "rgb(255 255 255 / 0.1)";
const HEADING = "#6e6e70";
const HAIRLINE = "rgb(255 255 255 / 0.1)";
const CHIP_BG = "rgb(255 255 255 / 0.03)";
const SIDEBAR_FIGMA_SCALE = 1.256;
const ACTIVE_BG =
  "linear-gradient(180deg, rgb(255 255 255 / 0) 0%, rgb(255 255 255 / 0.05) 100%), linear-gradient(90deg, rgb(255 255 255 / 0.05) 0%, rgb(255 255 255 / 0.05) 100%)";
const PROMO_BG =
  "linear-gradient(180deg, rgb(255 255 255 / 0) 0%, rgb(255 255 255 / 0.05) 100%), linear-gradient(90deg, rgb(136 166 255 / 0.04) 0%, rgb(136 166 255 / 0.04) 100%)";
const BANNER_CARD_BG =
  "linear-gradient(0.000005510236718464512deg, rgb(21 92 255 / 0.2) 0%, rgb(21 92 255 / 0) 100%), linear-gradient(90deg, rgb(255 255 255 / 0.03) 0%, rgb(255 255 255 / 0.03) 100%)";

function sidebarPx(value: number) {
  return value / SIDEBAR_FIGMA_SCALE;
}

/** Brand mark (favicon swoosh) tinted to any flat colour via CSS mask. */
function BrandMark({ width, color }: { width: number; color: string }) {
  return (
    <span
      className="block shrink-0"
      style={{
        width,
        height: width * (28 / 36),
        background: color,
        WebkitMaskImage: "url(/favicon.svg)",
        maskImage: "url(/favicon.svg)",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

/** Nav lead-icon, centred in a 20px box and tinted to the row's state. */
function NavIcon({ name, color = "rgb(255 255 255 / 0.5)", size = 18 }: { name: IconName; color?: string; size?: number }) {
  return (
    <span className="flex shrink-0 items-center justify-center" style={{ color, width: 20, height: 20 }}>
      <Icon name={name} size={size} />
    </span>
  );
}

/** Pill skeleton standing in for a nav item label. */
function SkelBar({ w }: { w: number }) {
  return <Bar w={w} h={11} style={{ background: SKELETON }} />;
}

/** A single nav row: lead icon + one or more label-skeleton segments. */
function NavItem({
  icon,
  widths,
  active = false,
}: {
  icon: IconName;
  widths: number[];
  active?: boolean;
}) {
  return (
    <div
      className="flex h-8 items-center gap-1.5 rounded-md p-1.5"
      style={active ? { backgroundImage: ACTIVE_BG, border: "1px solid rgb(255 255 255 / 0.05)" } : undefined}
    >
      <NavIcon name={icon} color={active ? "rgb(255 255 255 / 0.8)" : "rgb(255 255 255 / 0.5)"} />
      <div className="flex min-w-0 flex-1 items-center gap-1 px-1">
        {widths.map((w, i) => (
          <SkelBar key={i} w={w} />
        ))}
      </div>
    </div>
  );
}

/** A labelled (or unlabelled) group of nav rows. */
function NavGroup({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 px-4 py-2.5">
      {label && (
        <div className="flex items-center px-1.5 pb-1">
          <span className="text-[12px] font-medium leading-none" style={{ color: HEADING }}>
            {label}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

function SidebarBannerMark({ size }: { size: number }) {
  return (
    <svg
      className="block shrink-0"
      width={size}
      height={size}
      viewBox="0 0 20.098 20.098"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <path
        d="M6.54999 8.3877V11.1729C6.54999 11.4198 6.72491 11.7228 6.93868 11.8462L9.65964 13.4171C9.90015 13.5561 10.1966 13.5561 10.4371 13.4171L13.158 11.8462C13.3985 11.7074 13.5468 11.4507 13.5468 11.1729V8.3877C13.5468 8.10995 13.3985 7.8533 13.158 7.71442L10.4371 6.1435C10.1966 6.00462 9.90015 6.00462 9.65964 6.1435L6.93868 7.71442C6.69818 7.8533 6.54999 8.10995 6.54999 8.3877Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M10.5394 9.85059L12.5783 8.67386C12.729 8.58677 12.9175 8.69563 12.9175 8.86973V10.9855C12.9175 11.1245 12.8433 11.253 12.7229 11.3224L10.684 12.4995C10.5332 12.5866 10.3447 12.4777 10.3447 12.3036V10.1876C10.3447 10.0486 10.419 9.9201 10.5394 9.85059Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M11.0544 10.7846V13.5698C11.0544 13.8167 11.2293 14.1197 11.443 14.2431L14.164 15.814C14.4045 15.953 14.7009 15.953 14.9414 15.814L17.6624 14.2431C17.9029 14.1043 18.0511 13.8476 18.0511 13.5698V10.7846C18.0511 10.5068 17.9029 10.2502 17.6624 10.1113L14.9414 8.54041C14.7009 8.40157 14.4045 8.40157 14.164 8.54041L11.443 10.1113C11.2025 10.2502 11.0544 10.5068 11.0544 10.7846Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M2.04929 10.9916V13.7768C2.04929 14.0237 2.22421 14.3266 2.43798 14.4501L5.15898 16.021C5.39949 16.1599 5.69586 16.1599 5.93637 16.021L8.65733 14.4501C8.89784 14.3112 9.04606 14.0546 9.04606 13.7768V10.9916C9.04606 10.7138 8.89784 10.4572 8.65733 10.3183L5.93637 8.7474C5.69586 8.60847 5.39949 8.60847 5.15898 8.7474L2.43798 10.3183C2.19748 10.4572 2.04929 10.7138 2.04929 10.9916Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M6.54999 3.92427V6.70951C6.54999 6.95638 6.72491 7.25932 6.93868 7.38279L9.65964 8.95375C9.90015 9.09259 10.1966 9.09259 10.4371 8.95375L13.158 7.38279C13.3985 7.24392 13.5468 6.98727 13.5468 6.70951V3.92427C13.5468 3.64652 13.3985 3.38986 13.158 3.25099L10.4371 1.68006C10.1966 1.54119 9.90015 1.54119 9.65964 1.68006L6.93868 3.25099C6.69818 3.38986 6.54999 3.64652 6.54999 3.92427Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M11.0544 6.32121V9.10642C11.0544 9.35329 11.2293 9.65626 11.443 9.7797L14.164 11.3507C14.4045 11.4895 14.7009 11.4895 14.9414 11.3507L17.6624 9.7797C17.9029 9.64085 18.0511 9.38419 18.0511 9.10642V6.32121C18.0511 6.04345 17.9029 5.7868 17.6624 5.64793L14.9414 4.077C14.7009 3.93812 14.4045 3.93812 14.164 4.077L11.443 5.64793C11.2025 5.7868 11.0544 6.04345 11.0544 6.32121Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M2.04929 6.5289V9.31415C2.04929 9.56102 2.22421 9.86391 2.43798 9.98743L5.15898 11.5583C5.39949 11.6972 5.69586 11.6972 5.93637 11.5583L8.65733 9.98743C8.89784 9.8485 9.04606 9.59192 9.04606 9.31415V6.5289C9.04606 6.25115 8.89784 5.99449 8.65733 5.85562L5.93637 4.28469C5.69586 4.14582 5.39949 4.14582 5.15898 4.28469L2.43798 5.85562C2.19748 5.99449 2.04929 6.25115 2.04929 6.5289Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M6.54999 8.92584V11.7111C6.54999 11.958 6.72491 12.2609 6.93868 12.3844L9.65964 13.9553C9.90015 14.0942 10.1966 14.0942 10.4371 13.9553L13.158 12.3844C13.3985 12.2455 13.5468 11.9889 13.5468 11.7111V8.92584C13.5468 8.64807 13.3985 8.39141 13.158 8.25255L10.4371 6.68163C10.1966 6.54275 9.90015 6.54275 9.65964 6.68163L6.93868 8.25255C6.69818 8.39141 6.54999 8.64807 6.54999 8.92584Z"
        fill="#1D2E55"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
      <path
        d="M10.5394 10.3895L12.5783 9.21272C12.729 9.12571 12.9175 9.23449 12.9175 9.40859V11.5243C12.9175 11.6634 12.8433 11.7919 12.7229 11.8614L10.684 13.0385C10.5332 13.1255 10.3447 13.0167 10.3447 12.8426V10.7265C10.3447 10.5874 10.419 10.459 10.5394 10.3895Z"
        fill="#1A1D21"
        stroke="#2B5DF3"
        strokeWidth={0.468952}
        strokeMiterlimit={10}
      />
    </svg>
  );
}

function SidebarBannerCard() {
  return (
    <div
      className="flex shrink-0 items-center"
      data-node-id="10154:1115"
      style={{
        width: sidebarPx(256.249),
        height: sidebarPx(50.245),
        padding: sidebarPx(15.073),
        borderRadius: sidebarPx(7.537),
        border: "1px solid rgb(255 255 255 / 0.1)",
        backgroundImage: BANNER_CARD_BG,
      }}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex min-w-0 items-center" style={{ gap: sidebarPx(5.024) }}>
          <SidebarBannerMark size={sidebarPx(20.098)} />
          <div className="flex items-center" style={{ gap: sidebarPx(5.024) }}>
            <Bar
              w={sidebarPx(76.623)}
              h={sidebarPx(13.817)}
              style={{ background: SKELETON, borderRadius: 999 }}
            />
            <Bar
              w={sidebarPx(64.062)}
              h={sidebarPx(13.817)}
              style={{ background: SKELETON, borderRadius: 999 }}
            />
          </div>
        </div>
        <Icon
          name="IconSquareArrowTopRight"
          size={sidebarPx(17.586)}
          className="shrink-0 text-white/50"
        />
      </div>
    </div>
  );
}

function Sidebar() {
  return (
    <aside
      className="flex w-[236px] shrink-0 flex-col"
      style={{ borderRight: `1px solid ${HAIRLINE}` }}
    >
      {/* brand + collapse */}
      <div className="flex h-14 shrink-0 items-center justify-between px-5">
        <BrandMark width={26} color="rgb(255 255 255 / 0.92)" />
        <NavIcon name="IconChevronDoubleLeft" color="rgb(255 255 255 / 0.45)" />
      </div>

      {/* scrollable nav */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* notifications + tasks */}
        <NavGroup>
          <div className="flex h-8 items-center gap-1.5 rounded-md p-1.5">
            <NavIcon name="IconBell2" />
            <div className="flex min-w-0 flex-1 items-center px-1">
              <SkelBar w={85} />
            </div>
            <div
              className="ml-auto shrink-0 rounded-md"
              style={{
                width: 21,
                height: 20,
                background: "rgb(255 175 83 / 0.12)",
                border: "1px solid rgb(255 175 83 / 0.18)",
              }}
            />
          </div>
          <div className="flex h-8 items-center gap-1.5 rounded-md p-1.5">
            <NavIcon name="IconSquareChecklist" />
            <div className="flex min-w-0 flex-1 items-center gap-1.5 px-1">
              <SkelBar w={44} />
              <div className="size-2 shrink-0 rounded-full" style={{ background: "#ffaf53" }} />
            </div>
          </div>
        </NavGroup>

        <NavGroup label="Organize">
          <NavItem icon="IconFolderBookmarks" widths={[56]} active />
          <NavItem icon="IconBag" widths={[38]} />
        </NavGroup>

        <NavGroup label="Engage">
          <NavItem icon="IconSearchIntelligence" widths={[54, 49]} />
          <NavItem icon="IconPaperPlane" widths={[65]} />
          <NavItem icon="IconLayoutDashboard" widths={[51]} />
          <NavItem icon="IconScissors2" widths={[59]} />
          <NavItem icon="IconPrompt" widths={[25, 57]} />
        </NavGroup>

        <NavGroup label="Resources">
          <NavItem icon="IconSettingsGear2" widths={[52]} />
          <NavItem icon="IconGraduateCap" widths={[94]} />
          <NavItem icon="IconRescueRing" widths={[52, 67]} />
        </NavGroup>
      </div>

      {/* bottom promo card */}
      <div className="shrink-0 px-4 pb-1 pt-4">
        <div
          className="rounded-md"
          style={{ background: "rgb(255 255 255 / 0.04)", border: "1px solid rgb(255 255 255 / 0.05)" }}
        >
          <div className="px-1 pt-1">
            <div
              className="flex flex-col gap-1 rounded-md p-3"
              style={{ backgroundImage: PROMO_BG }}
            >
              <NavIcon name="IconPush" color="#6f8dff" />
              <SkelBar w={175} />
              <SkelBar w={132} />
            </div>
          </div>
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-1.5">
              <SkelBar w={61} />
              <SkelBar w={45} />
            </div>
            <NavIcon name="IconArrowRight" color="rgb(255 255 255 / 0.5)" size={16} />
          </div>
        </div>
      </div>

      {/* banner / upgrade card */}
      <div className="flex shrink-0 flex-col items-center gap-4 px-5 pb-6 pt-2">
        <div className="h-px" style={{ width: sidebarPx(256.249), background: HAIRLINE }} />
        <SidebarBannerCard />
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------- navbar */

/*
 * 1:1 rebuild of Figma node 10154:454. Skeleton breadcrumbs on the left; real
 * icons + sample text for the search field, project dropdown and workspace
 * chip on the right, then a round avatar button. Authored at the design's base
 * units (Figma px ÷ 1.256).
 */
function TopNav({
  organizationLogoUrl,
  organizationName,
  projectTitle,
}: {
  organizationLogoUrl: string;
  organizationName: string;
  projectTitle: string;
}) {
  return (
    <header
      className="flex h-16 shrink-0 items-center justify-between px-4"
      style={{ borderBottom: `1px solid ${HAIRLINE}` }}
    >
      {/* breadcrumbs */}
      <div className="flex items-center gap-0.5">
        <SkelBar w={25} />
        <NavIcon name="IconChevronRightSmall" color="rgb(255 255 255 / 0.45)" size={14} />
        <Bar w={50} h={11} style={{ background: "rgb(255 255 255 / 0.2)" }} />
      </div>

      {/* actions */}
      <div className="flex items-center gap-3">
        {/* search */}
        <div
          className="flex h-8 w-[165px] items-center gap-1.5 rounded-md px-2"
          style={{ background: CHIP_BG }}
        >
          <NavIcon name="IconMagnifyingGlass" color="rgb(255 255 255 / 0.5)" size={16} />
          <span className="flex-1 text-[14px] leading-none" style={{ color: "rgb(255 255 255 / 0.5)" }}>
            Search
          </span>
          <NavIcon name="IconRunShortcut" color="rgb(255 255 255 / 0.5)" size={16} />
        </div>

        {/* project title dropdown */}
        <div
          className="flex h-8 w-[180px] items-center justify-between rounded-md px-2"
          style={{ background: CHIP_BG }}
        >
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="size-4 shrink-0 rounded"
              style={{ background: "linear-gradient(135deg, #34d399 0%, #3b82f6 100%)" }}
            />
            <span className="truncate text-[14px] leading-none text-white">
              {projectTitle}
            </span>
          </div>
          <NavIcon name="IconChevronGrabberVertical" color="rgb(255 255 255 / 0.5)" size={16} />
        </div>

        {/* workspace chip */}
        <div
          className="flex h-8 items-center gap-1.5 rounded-md px-2"
          style={{ background: CHIP_BG }}
        >
          {organizationLogoUrl ? (
            <Avatar
              className="size-4 shrink-0 rounded-[3px]"
              style={{ border: "0.5px solid rgb(48 48 48 / 0.2)" }}
            >
              <AvatarImage src={organizationLogoUrl} alt="" />
              <AvatarFallback className="bg-[linear-gradient(135deg,#f0934d,#e8743b)]" />
            </Avatar>
          ) : (
            <div
              className="size-4 shrink-0 rounded-[3px]"
              style={{ background: "linear-gradient(135deg, #f0934d, #e8743b)", border: "0.5px solid rgb(48 48 48 / 0.2)" }}
            />
          )}
          <span className="text-[13px] font-medium leading-none tracking-[-0.13px] text-white">
            {organizationName}
          </span>
          <NavIcon name="IconSquareArrowTopRight" color="rgb(255 255 255 / 0.5)" size={16} />
        </div>

        {/* avatar */}
        <div
          className="size-8 shrink-0 rounded-full"
          style={{ background: "linear-gradient(135deg,#9ca3af,#4b5563)" }}
        />
      </div>
    </header>
  );
}

/* ------------------------------------------------------------- figma frame */

const NODE_BORDER = "rgb(255 255 255 / 0.1)";
const NODE_BORDER_DARK = "rgb(255 255 255 / 0.03)";
const NODE_PANEL = "rgb(24 24 26)";
const NODE_BLUE = "#2b5df3";

type TileSpec = {
  title: readonly number[];
  statusWidth: number;
  statusGreen?: boolean;
  hasImageBadge?: boolean;
};

const TILE_SPECS: TileSpec[] = [
  {
    title: [108.027, 81.648],
    statusWidth: 50.245,
    statusGreen: true,
    hasImageBadge: true,
  },
  { title: [42.708, 133.149, 81.648], statusWidth: 61.55 },
  { title: [79.136, 99.234, 81.648], statusWidth: 61.55 },
  { title: [79.136, 99.234, 81.648], statusWidth: 61.55 },
  { title: [79.136, 99.234, 81.648], statusWidth: 61.55 },
  { title: [79.136, 99.234, 81.648], statusWidth: 61.55 },
];

function px(value: number | string) {
  return typeof value === "number" ? `${value}px` : value;
}

function FigmaRect({
  w,
  h,
  alpha = 0.1,
  radius = 124.356,
  className,
}: {
  w: number | string;
  h: number;
  alpha?: number;
  radius?: number;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        width: px(w),
        height: h,
        borderRadius: radius,
        background: `rgb(255 255 255 / ${alpha})`,
      }}
    />
  );
}

function FigmaMainCanvas({
  projectCardTitle,
  projectCoverUrl,
}: {
  projectCardTitle: string;
  projectCoverUrl: string;
}) {
  return (
    <div
      className="relative min-h-0 flex-1 overflow-hidden"
      style={{ height: DASH_H - TOPNAV_H }}
    >
      <div
        className="absolute left-0 top-0"
        style={{
          width: FIGMA_MAIN_W,
          height: FIGMA_MAIN_H,
          transform: `scale(${FIGMA_MAIN_SCALE})`,
          transformOrigin: "top left",
        }}
      >
        <FigmaMainNode
          projectCardTitle={projectCardTitle}
          projectCoverUrl={projectCoverUrl}
        />
      </div>
    </div>
  );
}

function FigmaMainNode({
  projectCardTitle,
  projectCoverUrl,
}: {
  projectCardTitle: string;
  projectCoverUrl: string;
}) {
  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{
        width: FIGMA_MAIN_W,
        height: FIGMA_MAIN_H,
        background: "#0e0e10",
      }}
    >
      <div
        className="flex shrink-0 items-center"
        style={{
          height: 149.479,
          gap: 15.073,
          padding: "45.22px 20.098px",
          borderBottom: `1.256px solid ${NODE_BORDER}`,
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 5.024 }}>
          <FigmaRect w={87.929} h={22.61} alpha={0.2} />
          <FigmaRect w={605.451} h={13.817} />
        </div>
        <div
          className="shrink-0 rounded-[7.537px]"
          style={{
            width: 67.831,
            height: 35.171,
            border: `1.256px solid rgb(255 255 255 / 0.3)`,
            boxShadow: "0px 1.256px 2.512px 0px rgb(0 0 0 / 0.05)",
          }}
        />
        <div
          className="shrink-0 rounded-[7.537px]"
          style={{
            width: 143.198,
            height: 35.171,
            background: NODE_BLUE,
            boxShadow:
              "0px 1.256px 2.512px 0px rgb(0 0 0 / 0.05), inset 0px -1.256px 0px 0px rgb(0 0 0 / 0.08)",
          }}
        />
      </div>

      <div className="flex min-h-0 flex-1">
        <div
          className="flex shrink-0 flex-col overflow-hidden"
          style={{
            width: 1247.33,
            height: 1011.179,
            borderRight: `1.256px solid ${NODE_BORDER}`,
          }}
        >
          <FilterToolbar />
          <div
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
            style={{ borderTop: `1.256px solid ${NODE_BORDER}` }}
          >
            <div
              className="shrink-0 p-[20.098px]"
              style={{ height: 885.566 }}
            >
              <div
                className="flex flex-wrap items-start"
                style={{
                  width: 1207.134,
                  gap: "30.147px 30.147px",
                }}
              >
                {TILE_SPECS.map((spec, index) => (
                  <FigmaTile
                    key={index}
                    title={spec.title}
                    statusWidth={spec.statusWidth}
                    statusGreen={spec.statusGreen}
                    hasImageBadge={spec.hasImageBadge}
                    imageUrl={index === 0 ? projectCoverUrl : ""}
                    label={index === 0 ? projectCardTitle : ""}
                  />
                ))}
              </div>
            </div>
            <FigmaPaginator />
          </div>
        </div>
        <FigmaRightRail />
      </div>
    </div>
  );
}

function FilterToolbar() {
  return (
    <div
      className="flex shrink-0 items-center px-[20.098px] py-[15.073px]"
      style={{ height: 65.318 }}
    >
      <div className="flex min-w-0 flex-1 items-center" style={{ gap: 20.098 }}>
        {[121.844, 105.514, 105.514].map((width, index) => (
          <div
            key={index}
            className="flex shrink-0 items-center justify-center rounded-full border-dashed"
            style={{
              width,
              height: 35.171,
              gap: 5.024,
              padding: "5.024px 10.049px",
              background: "rgb(255 255 255 / 0.02)",
              border: `1.256px dashed ${NODE_BORDER}`,
            }}
          >
            <Icon
              name="IconFilter2"
              size={20.098}
              className="shrink-0 text-white/45"
            />
            <div className="shrink-0" style={{ width: index === 0 ? 76.623 : 60.294, height: 25.122 }} />
          </div>
        ))}
        <div
          className="flex shrink-0 items-center overflow-hidden rounded-[6.281px]"
          style={{
            width: 276.347,
            height: 35.171,
            background: "rgb(255 255 255 / 0.03)",
            padding: "7.537px 10.049px",
          }}
        >
          <Icon
            name="IconMagnifyingGlass"
            size={20.098}
            className="shrink-0 text-white/45"
          />
        </div>
      </div>
    </div>
  );
}

function FigmaTile({
  title,
  statusWidth,
  statusGreen,
  hasImageBadge,
  imageUrl,
  label,
}: {
  title: readonly number[];
  statusWidth: number;
  statusGreen?: boolean;
  hasImageBadge?: boolean;
  imageUrl: string;
  label: string;
}) {
  return (
    <div
      className="flex shrink-0 flex-col items-start"
      style={{ width: 382.28, height: 342.921 }}
    >
      <div
        className="relative w-full shrink-0 overflow-hidden rounded-t-[15.073px]"
        style={{
          height: 167.064,
          background: "rgb(255 255 255 / 0.1)",
        }}
      >
        {imageUrl ? (
          <Avatar className="absolute inset-0 size-full rounded-none">
            <AvatarImage src={imageUrl} alt="" />
            <AvatarFallback className="rounded-none bg-transparent" />
          </Avatar>
        ) : null}
        {hasImageBadge ? (
          <div
            className="absolute flex items-center justify-center overflow-hidden rounded-[7.537px]"
            style={{
              left: 13.817,
              top: 15.073,
              width: 79.136,
              height: 25.122,
              padding: 2.512,
              background: NODE_PANEL,
              backdropFilter: "blur(2.512px)",
            }}
          >
            <div style={{ width: 74.111, height: 20.098 }} />
          </div>
        ) : null}
      </div>
      <div
        className="relative flex w-full shrink-0 flex-col justify-between rounded-b-[15.073px]"
        style={{
          height: 175.857,
          padding: 20.098,
          border: `1.256px solid ${NODE_BORDER}`,
          background: "rgb(255 255 255 / 0.03)",
          boxShadow:
            "0px 1.256px 2.512px 0px rgb(0 0 0 / 0.05), inset 0px -1.256px 0px 0px rgb(0 0 0 / 0.1)",
        }}
      >
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center" style={{ gap: 5.024 }}>
            <div
              className="flex items-center rounded-[7.537px]"
              style={{
                width: 143.198,
                height: 25.122,
                padding: 2.512,
                background: "rgb(255 255 255 / 0.03)",
                backdropFilter: "blur(2.512px)",
              }}
            >
              <div
                className="flex items-center justify-center"
                style={{ width: 20.098, height: 20.098 }}
              >
                <div
                  className="rounded-[2.512px]"
                  style={{
                    width: 15.073,
                    height: 15.073,
                    background: "rgb(255 255 255 / 0.1)",
                    boxShadow:
                      "0px 1.005px 2.01px 0px rgb(0 0 0 / 0.05), inset 0px -1.005px 0px 0px rgb(0 0 0 / 0.08)",
                  }}
                />
              </div>
              <div style={{ width: 118.076, height: 20.098 }} />
            </div>
            <div
              className="rounded-[7.537px]"
              style={{
                width: statusWidth + 5.024,
                height: 25.122,
                padding: 2.512,
                background: statusGreen
                  ? "rgb(102 220 126 / 0.1)"
                  : "rgb(255 255 255 / 0.1)",
                border: `1.256px solid ${NODE_BORDER}`,
              }}
            />
          </div>
          <div className="flex items-center justify-center gap-[3px] opacity-80" style={{ width: 25.122, height: 25.122 }}>
            <span className="size-[3px] rounded-full bg-white/60" />
            <span className="size-[3px] rounded-full bg-white/60" />
            <span className="size-[3px] rounded-full bg-white/60" />
          </div>
        </div>

        <div className="flex flex-col" style={{ gap: 5.024 }}>
          <div className="flex items-center justify-between" style={{ width: 300.213 }}>
            <div className="flex items-center" style={{ gap: 5.024 }}>
              {label ? (
                <span
                  className="max-w-[265px] truncate text-[17px] font-medium leading-[22.61px] text-white"
                  title={label}
                >
                  {label}
                </span>
              ) : (
                title.map((width, index) => (
                  <FigmaRect key={index} w={width} h={22.61} alpha={0.15} />
                ))
              )}
            </div>
            <div className="flex items-center justify-center" style={{ width: 25.122, height: 25.122 }}>
              <Icon name="IconArrowUpRight" size={18} className="text-white/45" />
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 5.024, height: 13.817 }}>
            <FigmaRect w={188.418} h={13.817} />
            <span className="size-[3.9px] rounded-full bg-white/20" />
            <FigmaRect w={50.245} h={13.817} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FigmaPaginator() {
  return (
    <div
      className="flex shrink-0 items-center justify-between"
      style={{
        height: 60.294,
        padding: "0 20.098px",
        borderTop: `1.256px solid ${NODE_BORDER}`,
        background:
          "linear-gradient(90deg, rgb(255 255 255 / 0.01) 0%, rgb(255 255 255 / 0.01) 100%), #18181a",
      }}
    >
      <FigmaRect w={62.806} h={13.817} />
      <div className="flex items-center" style={{ gap: 15.073 }}>
        <Icon name="IconChevronDoubleLeft" size={35.171} className="text-white/30" />
        <Icon name="IconChevronLeft" size={21.354} className="text-white/30" />
        {[0, 1, 2, 3].map((item) => (
          <div
            key={item}
            className="shrink-0 rounded-[7.537px]"
            style={{
              width: 35.171,
              height: 35.171,
              background: "rgb(255 255 255 / 0.02)",
              border: `1.256px solid rgb(255 255 255 / 0.15)`,
            }}
          />
        ))}
        <Icon name="IconChevronRight" size={21.354} className="text-white/30" />
        <Icon name="IconChevronDoubleRight" size={35.171} className="text-white/30" />
      </div>
    </div>
  );
}

function FigmaRightRail() {
  return (
    <div
      className="shrink-0"
      style={{
        width: 455.972,
        height: 1011.179,
        padding: 20.098,
      }}
    >
      <div className="flex flex-col" style={{ gap: 20.098, width: 415.777 }}>
        <FigmaRect w={249.968} h={11.305} />
        {GRADIENTS.map((gradient, index) => (
          <FigmaVideoCard key={index} gradient={gradient} />
        ))}
      </div>
    </div>
  );
}

function FigmaVideoCard({ gradient }: { gradient: string }) {
  return (
    <div
      className="relative flex items-center rounded-[10.049px]"
      style={{
        width: 415.777,
        height: 123.1,
        padding: 20.098,
        border: `1.256px solid ${NODE_BORDER_DARK}`,
        background: "rgb(255 255 255 / 0.03)",
        boxShadow:
          "0px 1.256px 2.512px 0px rgb(0 0 0 / 0.05), inset 0px -1.256px 0px 0px rgb(0 0 0 / 0.1)",
      }}
    >
      <div className="flex items-center" style={{ gap: 15.073 }}>
        <div
          className="relative shrink-0 overflow-hidden rounded-[10.049px]"
          style={{
            width: 131.893,
            height: 82.904,
            background: gradient,
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="absolute flex items-center justify-center"
            style={{
              left: 56.526,
              top: 32.031,
              width: 20.098,
              height: 20.098,
            }}
          >
            <div className="flex size-5 items-center justify-center rounded-full border border-white/80">
              <span
                className="ml-[1px] h-0 w-0"
                style={{
                  borderTop: "4px solid transparent",
                  borderBottom: "4px solid transparent",
                  borderLeft: "6px solid rgb(255 255 255 / 0.9)",
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-end" style={{ gap: 15.073, width: 228.614 }}>
          <FigmaRect w={160.784} h={13.817} alpha={0.2} />
          <div className="flex flex-col" style={{ gap: 7.537 }}>
            <FigmaRect w={212.285} h={11.305} />
            <FigmaRect w={157.015} h={11.305} />
          </div>
        </div>
      </div>
    </div>
  );
}
